import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import useSupercluster from 'use-supercluster';
import styles from './styles';
import { HubActivationMap } from '../../redux/types/hubs';
import { propertyStatuses } from '../../constants/property';

const mapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#e0e0e0' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#4a4a4a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#e0e0e0' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f0f0f0' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#a0b4c8' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#e8e8e8' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#d8d8d8' }] },
];

const MAP_OPTIONS: google.maps.MapOptions = {
  styles: mapStyles,
  disableDefaultUI: false,
  zoomControl: true,
};

const getStatusColor = (action: string) => {
  switch (action) {
    case 'activate': return '#43a047';
    default: return '#e53935';
  }
};

const getDarkStatusColor = (action: string) => {
  switch (action) {
    case 'activate': return '#2e7d32';
    default: return '#b71c1c';
  }
};

const SUPERCLUSTER_OPTIONS = { radius: 75, maxZoom: 20 };

type Props = {
  logs: HubActivationMap[];
  handleHubSelect: (serialNumber: string) => void;
} & WithStyles<typeof styles>;

export const GMap = ({ classes, logs, handleHubSelect }: Props) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [zoom, setZoom] = useState(15);
  const [bounds, setBounds] = useState<[number, number, number, number] | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY ?? '',
    language: 'en',
  });

  const points = useMemo(
    () => logs.map((m) => ({
      type: 'Feature' as const,
      properties: {
        markerId: m.id,
        label: m.serial_number,
        cluster: false,
        action: m.action,
        propertyStatus: m.propertyStatus,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [Number(m.longitude), Number(m.latitude)],
      },
    })),
    [logs],
  );

  const fitMapToBounds = useCallback((map: google.maps.Map) => {
    if (!logs.length) return;
    const b = new window.google.maps.LatLngBounds();
    logs.forEach((m) => b.extend(new window.google.maps.LatLng(m.latitude, m.longitude)));
    map.fitBounds(b);
  }, [logs]);

  useEffect(() => {
    if (mapRef.current) fitMapToBounds(mapRef.current);
  }, [fitMapToBounds]);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      fitMapToBounds(map);
    },
    [fitMapToBounds],
  );

  const handleMapChange = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const z = map.getZoom();
    const b = map.getBounds();
    if (z !== undefined) setZoom(z);
    if (b) {
      setBounds([
        b.getSouthWest().lng(),
        b.getSouthWest().lat(),
        b.getNorthEast().lng(),
        b.getNorthEast().lat(),
      ]);
    }
  }, []);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: SUPERCLUSTER_OPTIONS,
  });

  const handleMarkerClick = useCallback(
    (markerId: number, serialNumber: string, lat: number, lng: number) => {
      if (selectedId === markerId) {
        setSelectedId(null);
        return;
      }
      setSelectedId(markerId);
      if (mapRef.current) {
        mapRef.current.panTo({ lat, lng });
      }
      handleHubSelect(serialNumber);
    },
    [selectedId, handleHubSelect],
  );

  const handleClusterClick = useCallback(
    (clusterId: number, lat: number, lng: number) => {
      const expansionZoom = Math.min(
        supercluster!.getClusterExpansionZoom(clusterId),
        20,
      );
      if (mapRef.current) {
        mapRef.current.setZoom(expansionZoom);
        mapRef.current.panTo({ lat, lng });
      }
    },
    [supercluster],
  );

  if (!isLoaded) return <p>Loading...</p>;

  const getPropertyStatusLabel = (catalogId: string) => propertyStatuses.find((s) => s.catalog_id === catalogId)?.value ?? catalogId;

  return (
    <div className={classes.mapWrapper}>
      <GoogleMap
        mapContainerClassName={classes.mapContainer}
        zoom={zoom}
        options={MAP_OPTIONS}
        onLoad={onLoad}
        onZoomChanged={handleMapChange}
        onBoundsChanged={handleMapChange}
      >
        {clusters.map((cluster, index) => {
          const [lng, lat] = cluster.geometry.coordinates;
          const {
            cluster: isCluster, point_count, markerId, label, action, propertyStatus,
          } = cluster.properties;

          if (isCluster) {
            return (
              <OverlayView
                key={`cluster-${index}`}
                position={{ lat, lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <button
                  type="button"
                  className={classes.cluster}
                  onClick={() => handleClusterClick(cluster.id as number, lat, lng)}
                >
                  {point_count}
                </button>
              </OverlayView>
            );
          }

          return (
            <OverlayView
              key={`marker-${markerId}`}
              position={{ lat, lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className={classes.markerWrapper}>
                <button
                  type="button"
                  className={classes.marker}
                  onClick={() => handleMarkerClick(markerId, label, lat, lng)}
                >
                  <div className={`${classes.markerInner} ${selectedId === markerId ? classes.markerActive : ''}`}>
                    <div
                      className={classes.markerLabel}
                      style={{ background: getStatusColor(action) }}
                    >
                      {label}
                    </div>
                    <div
                      className={classes.markerStatus}
                      style={{ background: getDarkStatusColor(action) }}
                    >
                      {getPropertyStatusLabel(propertyStatus)}
                    </div>
                  </div>
                </button>
              </div>
            </OverlayView>
          );
        })}
      </GoogleMap>
    </div>
  );
};
